import * as Tooltip from '@radix-ui/react-tooltip';
import { LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import type * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  type NavRole,
  visibleNavItems,
} from '../../constants/navigationItems.constants';
import { useAuth } from '../../context/AuthContext';
import { trackEvent } from '../../helpers/analytics';
import { isRouteActive, resolveNavRoute } from '../../helpers/resolveNavRoute';
import { useLogoutFlow } from '../../hooks/useLogoutFlow';
import { useNavCollapsed } from '../../hooks/useNavCollapsed';
import { NavigationItem } from './NavigationItem';
import { PendingRewriteBell } from './PendingRewriteBell';
import './NavigationRail.scss';

export function NavigationRail(): React.ReactElement {
  const { user } = useAuth();
  const role = ((user as { role?: string } | null)?.role ??
    null) as NavRole | null;
  const username =
    (user as { username?: string | null } | null)?.username ?? null;
  const { collapsed, toggle } = useNavCollapsed();
  // TRIL-146: Profile is no longer in the rail (moved to AvatarMenu).
  // The plan-c pending dot will follow the Profile entry into the
  // AvatarMenu in a future iteration.
  const location = useLocation();
  const navigate = useNavigate();

  const items = visibleNavItems(role);
  const triggerLogout = useLogoutFlow();

  const handleToggle = (): void => {
    const newState = collapsed ? 'expanded' : 'collapsed';
    trackEvent('nav.rail.toggled', { newState });
    toggle();
  };

  const handleLogout = (): void => {
    trackEvent('nav.item.clicked', {
      route: 'logout',
      source: collapsed ? 'rail_collapsed' : 'rail',
    });
    void triggerLogout();
  };

  const handleNavigate = (template: string): void => {
    const resolved = resolveNavRoute(template, username);
    // Defensive: if the template needed a username but we don't have one yet
    // (brief auth-loading window before the rail unmounts), don't dead-end
    // the user on a literal ':username' URL.
    if (resolved.includes(':username')) return;
    trackEvent('nav.item.clicked', {
      route: resolved,
      source: collapsed ? 'rail_collapsed' : 'rail',
    });
    navigate(resolved);
  };

  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const toggleLabel = collapsed ? 'Expand navigation' : 'Collapse navigation';

  return (
    <Tooltip.Provider delayDuration={150}>
      <nav
        className={`NAVIGATION__RAIL ${collapsed ? 'NAVIGATION__RAIL--collapsed' : 'NAVIGATION__RAIL--expanded'}`}
        aria-label="Primary"
      >
        {/* TRIL-291: the pending-rewrite callout sits at the very top of the
            rail, above the Collapse toggle — its flamme treatment is
            deliberately distinct, so it reads as its own thing, separate from
            the rail controls and nav items. */}
        <PendingRewriteBell collapsed={collapsed} />
        <button
          type="button"
          className="NAVIGATION__RAIL__TOGGLE"
          onClick={handleToggle}
          aria-label={toggleLabel}
        >
          <ToggleIcon size={20} aria-hidden="true" />
          {!collapsed && (
            <span className="NAVIGATION__RAIL__TOGGLE__LABEL">Collapse</span>
          )}
        </button>
        <ul className="NAVIGATION__RAIL__LIST">
          {items.map((item) => {
            const resolvedRoute = resolveNavRoute(item.route, username);
            const isActive = isRouteActive(location.pathname, resolvedRoute);
            return (
              <li key={item.route} className="NAVIGATION__RAIL__LIST__ITEM">
                <NavigationItem
                  route={item.route}
                  label={item.label}
                  icon={item.icon}
                  isActive={isActive}
                  collapsed={collapsed}
                  onClick={() => handleNavigate(item.route)}
                />
              </li>
            );
          })}
        </ul>
        {/* TRIL-146: Logout as the final rail item — icon + tooltip in
            collapsed mode, icon + label in expanded mode. Same visual
            contract as the route items above. */}
        <div className="NAVIGATION__RAIL__FOOTER">
          <NavigationItem
            route="/log-in"
            label="Sign out"
            icon={LogOut}
            isActive={false}
            collapsed={collapsed}
            onClick={handleLogout}
          />
        </div>
      </nav>
    </Tooltip.Provider>
  );
}
